import { useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  createPreRegistration,
  getLandingPageBySlug,
} from "../../api/landingPageApi";
import Select from "react-select";
import { allCountries } from "country-telephone-data";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import {
  GoogleMap,
  Marker,
  LoadScript,
  useLoadScript,
} from "@react-google-maps/api";
import { serviceKeys } from "../../constants";
import map_pin from "../../assets/map_pin.png";
import NotFound from "../notFound/NotFoundPage";

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const countryOptions = allCountries.map((country) => ({
  label: `${getFlagEmoji(country.iso2)} ${country.name} (+${country.dialCode})`,
  value: `+${country.dialCode}`,
}));

// const countryOptions = allCountries.map((country) => ({
//   label: `${country.name} (+${country.dialCode})`,
//   value: `+${country.dialCode}`,
// }));

const RESERVED_ROUTES = [
  "login",
  "users",
  "artists",
  "landing-pages",
  "bookings",
  "clients",
];

const PublicLandingPage = () => {
  const { slug } = useParams();

  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const [successData, setSuccessData] = useState(null);
  const [countdown, setCountdown] = useState(3);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: serviceKeys.API_KEY,
  });

  useEffect(() => {
    getLandingPageBySlug(slug)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+34",
    acceptTerms: false,
    acceptPrivacy: false,
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePhoneChange = (value) => {
    // Remove non-digits
    let cleaned = value.replace(/\D/g, "");

    handleChange("phone", cleaned);
  };

  const isFormValid =
    form.name &&
    form.email &&
    form.phone &&
    form.acceptTerms &&
    form.acceptPrivacy;

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      setSubmitting(true);

      const formattedPhone = form.phone.replace(/^0+/, "");

      const payload = {
        full_name: form.name,
        email: form.email,
        phone: `${form.countryCode}${formattedPhone}`,
        accepted_terms: true,
        accepted_privacy: true,
      };

      // await createPreRegistration(slug, payload);

      // setToast({
      //   type: "success",
      //   message: "Registration successful!",
      // });

      await createPreRegistration(slug, payload);

      setSuccessData({
        whatsapp_link: data.whatsapp_link,
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        countryCode: "+34",
        acceptTerms: false,
        acceptPrivacy: false,
      });
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!successData) return;

    if (countdown <= 0) return;

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [successData, countdown]);

  useEffect(() => {
    if (!successData || countdown !== 0) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = successData.whatsapp_link;
    } else {
      window.open(successData.whatsapp_link, "_blank");
    }
  }, [countdown, successData]);

  // 🔒 Prevent conflicts with system routes
  if (RESERVED_ROUTES.includes(slug)) {
    return <Navigate to="/" replace />;
  }

  if (notFound) {
    return <NotFound />;
  }

  if (!data) {
    return <div className="text-white p-10">Loading...</div>;
  }

  if (successData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl p-6 text-center shadow-xl">
          {/* close */}
          <button
            onClick={() => setSuccessData(null)}
            className="absolute top-3 right-3 text-slate-400 hover:text-white text-lg"
          >
            ✕
          </button>

          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-pink-600/20">
            <span className="text-2xl text-pink-500">✓</span>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-5">Thank You!</h1>

          {/* Redirect text */}
          <p className="text-slate-300 font-bold text-2xl mb-2">
            Redirecting to WhatsApp...
          </p>

          <p className="text-slate-400 text-sm mb-4">
            Please send the message on WhatsApp <br /> to receive your ticket
            link.
          </p>

          {/* Countdown */}
          <div className="text-3xl font-bold text-pink-500 mb-6">
            {countdown}
          </div>

          {/* Manual button */}
          {/* {countdown === 0 && (
            <a
              href={data.whatsapp_link}
              className="inline-block w-full py-3 rounded-lg bg-pink-600 hover:bg-pink-700 text-white font-medium transition"
            >
              Continue Now!
            </a>
          )} */}
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-[#05261c] border border-green-900/30 rounded-xl h-[200px] flex items-center justify-center">
        <p className="text-green-400">Loading map...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-6 md:px-10 md:py-10 text-center">
      {/* Flyer */}
      {data.flyer && (
        <img
          src={data.flyer}
          alt="flyer"
          className="w-full max-h-[250px] md:max-h-[400px] object-contain rounded-xl"
        />
      )}

      {/* Content */}
      <div className="max-w-2xl mx-auto px-2 md:px-6 py-6 md:py-10">
        <h1 className="text-3xl font-bold">{data.title}</h1>

        <p className="mt-4 text-slate-300 whitespace-pre-line">{data.info}</p>
      </div>

      {data.map_latitude && data.map_longitude && (
        <div className="max-w-3xl mx-auto mb-6 bg-slate-900 border border-white/10 rounded-xl p-4 md:p-5 text-left">
          {/* Address */}
          <p className="text-sm text-slate-400 mb-2">Location: </p>
          <p className="text-white mb-4">{data.map_address}</p>

          {/* Map */}
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "250px" }}
            center={{
              lat: Number(data.map_latitude),
              lng: Number(data.map_longitude),
            }}
            zoom={14}
          >
            <Marker
              position={{
                lat: Number(data.map_latitude),
                lng: Number(data.map_longitude),
              }}
              icon={{
                url: map_pin,
                scaledSize: new window.google.maps.Size(60, 60),
              }}
            />
          </GoogleMap>
        </div>
      )}

      <div>
        <p className="text-base font-medium text-slate-300 mb-5">
          Register and you’ll be automatically redirected to WhatsApp to receive
          the ticket link.
        </p>
      </div>

      <div className="max-w-3xl mx-auto mt-4 bg-slate-900 border border-white/10 rounded-xl p-4 md:p-6 space-y-5">
        {/* Name + Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Nombres & Apellidos (Full Name)
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>
        </div>

        {/* Phone */}
        <div className="text-left">
          <label className="block text-sm text-slate-300 mb-1">
            Whatsapp Number
          </label>

          <div className="flex flex-col sm:flex-row gap-2">
            {/* Country Code Dropdown */}
            <div className="w-full sm:w-40">
              <Select
                options={countryOptions}
                value={countryOptions.find(
                  (opt) => opt.value === form.countryCode,
                )}
                onChange={(selected) =>
                  handleChange("countryCode", selected.value)
                }
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            {/* Phone Input */}
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              value={form.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="Enter phone number"
              className="flex-1 bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3 text-left text-sm text-slate-300">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) => handleChange("acceptTerms", e.target.checked)}
              className="mt-1 accent-pink-600"
            />
            <span>
              I agree to the{" "}
              <span className="text-pink-500 underline cursor-pointer">
                Terms & Conditions
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acceptPrivacy}
              onChange={(e) => handleChange("acceptPrivacy", e.target.checked)}
              className="mt-1 accent-pink-600"
            />
            <span>
              I agree to the{" "}
              <span className="text-pink-500 underline cursor-pointer">
                Privacy Policy
              </span>
            </span>
          </label>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!isFormValid}
          className={`w-full py-3 rounded-lg font-medium transition ${
            isFormValid
              ? "bg-pink-600 hover:bg-pink-700"
              : "bg-pink-200 cursor-not-allowed"
          }`}
        >
          Confirm on WhatsApp
        </button>

        <div>
          <p className="text-sm font-medium text-slate-300 mb-5">
            After clicking, please wait a few seconds. You’ll be redirected to
            our WhatsApp.
          </p>
        </div>
      </div>

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default PublicLandingPage;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#131C31",
    borderColor: state.isFocused
      ? "rgba(236, 72, 153, 0.6)" // pink focus
      : "rgba(255,255,255,0.1)",
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(236, 72, 153, 0.6)",
    },
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: "#131C31",
    border: "1px solid rgba(255,255,255,0.1)",
    zIndex: 50,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(236, 72, 153, 0.2)"
      : state.isSelected
        ? "rgba(236, 72, 153, 0.4)"
        : "transparent",
    color: "#fff",
    cursor: "pointer",
  }),

  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),

  input: (base) => ({
    ...base,
    color: "#fff",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#94a3b8", // slate-400
  }),

  dropdownIndicator: (base) => ({
    ...base,
    color: "#94a3b8",
    "&:hover": {
      color: "#fff",
    },
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),
};
